import { useEffect, useState } from 'react';
import { Modal, Form, Input, InputNumber, Select, Row, Col, Divider, DatePicker, Tabs, Button, message } from '../../ui';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermission } from '../../store/auth';
import { FileUploadField } from '../../components/FileUploadField';
import { LocationPicker } from '../../components/LocationPicker';
import { STATE_OPTIONS } from '../../constants/indianStates';
import { mobileRules, gstinRules, panRules, pincodeRules, emailRules, upperCase } from '../../lib/validators';
import { PhoneInput } from '../../components/PhoneInput';
import { retailerStatusLabel } from '../../lib/statusTone';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Pass record to edit; omit/undefined to create new. */
  record?: any;
}

const TAB_KEYS = ['basics', 'location', 'documents'];
// Fields living on each tab — used to route validation errors to the right tab.
const LOCATION_FIELDS = ['addressLine1', 'city', 'stateCode', 'pincode', 'latitude', 'longitude', 'territoryId', 'assignedSalesPersonId', 'creditLimit', 'creditPeriodDays', 'discountType', 'generalDiscountPct'];
const DOCUMENT_FIELDS = ['photoUrl', 'gstCertUrl', 'panCardUrl', 'agriLicenseUrl', 'agreementUrl', 'agreementDate', 'agreementExpiry'];
const fieldTab = (name: string) =>
  LOCATION_FIELDS.includes(name) ? 'location' : DOCUMENT_FIELDS.includes(name) ? 'documents' : 'basics';

const STATUS_OPTIONS = ['PROSPECT', 'KYC_PENDING', 'ACTIVE', 'SUSPENDED', 'CLOSED']
  .map((value) => ({ value, label: retailerStatusLabel(value) }));

// The form's values → the request body. An edit shapes its starting values the same way, so the two compare like for like
// (`22.5125570` from the record and `22.512557` from the map are the same pin; a date string and a picker value the same day).
const toPayload = (values: any) => ({
  ...values,
  // Blank code → auto-assigned server-side (R-000001…). An empty string would slip past the
  // backend's `input.code ?? nextCode()`, so normalise it to undefined here.
  code: values.code?.trim() || undefined,
  creditLimit: Number(values.creditLimit ?? 0),
  creditPeriodDays: Number(values.creditPeriodDays ?? 0),
  latitude: String(values.latitude ?? 0),
  longitude: String(values.longitude ?? 0),
  agreementDate: values.agreementDate ? values.agreementDate.format('YYYY-MM-DD') : undefined,
  agreementExpiry: values.agreementExpiry ? values.agreementExpiry.format('YYYY-MM-DD') : undefined,
});

/**
 * AN EDIT SENDS WHAT CHANGED, NOTHING ELSE. The form holds every field of the dealer — the code and status the server
 * owns, the credit terms only an Admin may write — and used to PATCH all of them back. The server refuses a patch that
 * touches a field the person may not write, so every save from this form was refused, even a pin moved on the map
 * (R-000871, 15 Sep 2026). Only the fields the form submitted are compared; one cleared is sent as null so the clearing saves.
 */
const changedFields = (before: Record<string, any>, after: Record<string, any>, fields: string[]) => {
  const norm = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim());
  return Object.fromEntries(fields
    .filter((k) => norm(after[k]) !== norm(before[k]))
    .map((k) => [k, after[k] === undefined ? null : after[k]]));
};

export function RetailerFormModal({ open, onClose, record }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const isEdit = !!record;
  const [tab, setTab] = useState('basics');
  const discountType = Form.useWatch('discountType', form);

  // Only territories the actor works in (full-access roles get all). A Sales Person
  // therefore can't place a retailer outside their assigned beats/districts.
  const { data: territories } = useQuery({
    queryKey: ['my-territories'],
    queryFn: () => request<any[]>({ url: '/territory-assignments/me/territories' }),
    enabled: open,
  });

  // The full staff directory is only the FALLBACK owner list (used before a territory is picked, and to
  // keep an out-of-scope owner visible on an edit). It needs USER.VIEW, which a Sales Person does not
  // hold — asking anyway 403'd every time a rep opened this form. They always have a territory, so
  // `/territory-assignments/sales-persons` below gives them the list that matters.
  const canViewUsers = usePermission('USER.VIEW');
  const canVerifyKyc = usePermission('RETAILER.KYC.VERIFY');
  const { data: salesUsers } = useQuery({
    queryKey: ['sales-persons-list'],
    queryFn: () => requestAllPages('/users', { role: 'SALES_PERSON' }),
    enabled: open && canViewUsers,
  });

  // Constrain "Assigned Sales Person" to whoever works the selected territory (bound to it or
  // an ancestor). defaultOwnerId = the territory's primary, pre-selected as the owner.
  const territoryId = Form.useWatch('territoryId', form);
  const assignedSpId = Form.useWatch('assignedSalesPersonId', form);
  const { data: spForTerr, isFetching: spLoading } = useQuery({
    queryKey: ['sps-for-territory', territoryId],
    queryFn: () => request<{ salesPersons: Array<{ id: string; fullName: string; territory?: string }>; defaultOwnerId: string | null }>({ url: '/territory-assignments/sales-persons', params: { territoryId } }),
    enabled: open && !!territoryId,
  });
  const allSpOptions = (salesUsers?.data ?? []).map((u: any) => ({ value: String(u.id), label: u.fullName }));
  const scopedSps = territoryId && spForTerr?.salesPersons?.length ? spForTerr.salesPersons : null;
  // Show each SP with the territory that puts them here, e.g. "Asmita Kale — MH-PUNE-EAST · Pune East Beat".
  let spOptions = scopedSps
    ? scopedSps.map((s) => ({ value: String(s.id), label: s.territory ? `${s.fullName} — ${s.territory}` : s.fullName }))
    : allSpOptions;
  // Keep a currently-selected owner visible even if they're outside the scoped list (edits).
  if (assignedSpId && !spOptions.some((o: any) => o.value === String(assignedSpId))) {
    const found = allSpOptions.find((o: any) => o.value === String(assignedSpId));
    if (found) spOptions = [...spOptions, found];
  }
  // Pre-select the territory's primary SP when a territory is picked and the field is empty.
  useEffect(() => {
    if (!open || !territoryId) return;
    const def = spForTerr?.defaultOwnerId;
    if (def && !form.getFieldValue('assignedSalesPersonId')) form.setFieldValue('assignedSalesPersonId', String(def));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, territoryId, spForTerr?.defaultOwnerId]);

  // A retailer belongs to a beat (or district) — hide zones/states. Keep the record's current
  // territory visible on edit even if it's a higher level (legacy data).
  let territoryOptions = (territories ?? [])
    .filter((t: any) => t.level === 'district' || t.level === 'beat')
    .map((t: any) => ({ value: String(t.id), label: `${t.code} · ${t.name}` }));
  if (record?.territoryId && !territoryOptions.some((o: any) => o.value === String(record.territoryId))) {
    const cur = (territories ?? []).find((t: any) => String(t.id) === String(record.territoryId));
    if (cur) territoryOptions = [...territoryOptions, { value: String(cur.id), label: `${cur.code} · ${cur.name}` }];
  }

  // Reset to the first tab whenever the modal opens. Field seeding is handled by the Form's
  // initialValues + a per-target key (below) so it can't race the Radix-modal remount.
  useEffect(() => {
    if (open) setTab('basics');
  }, [open]);

  const save = useConfirmedMutation({ title: "Save this retailer?", okText: "Yes, save" }, {
    mutationFn: (payload: any) => (isEdit
      ? request({ url: `/retailers/${record.id}`, method: 'PATCH', data: payload })
      : request({ url: '/retailers', method: 'POST', data: payload })),
    onSuccess: () => {
      message.success(isEdit ? 'Retailer updated' : 'Retailer created');
      qc.invalidateQueries({ queryKey: ['retailers'] });
      if (isEdit) qc.invalidateQueries({ queryKey: ['retailer', record.id] });
      onClose();
    },
    onError: (e: any) => {
      message.error(e.response?.data?.error?.message ?? 'Failed');
    },
  });

  // Wizard footer: Back / Next while creating (Create on the last tab); plain Update for edits.
  const tabIndex = TAB_KEYS.indexOf(tab);
  const isLastTab = tabIndex === TAB_KEYS.length - 1;
  const goNext = () => {
    const fields = tab === 'basics'
      ? ['companyName', 'contactPerson', 'mobile']
      : tab === 'location'
        ? ['addressLine1', 'city', 'stateCode', 'pincode', 'latitude', 'longitude', 'territoryId']
        : [];
    form.validateFields(fields)
      .then(() => { if (tabIndex < TAB_KEYS.length - 1) setTab(TAB_KEYS[tabIndex + 1]); })
      .catch(() => { /* errors shown inline on the field */ });
  };
  const footer = isEdit
    ? [
        <Button key="cancel" variant="default" onClick={onClose}>Cancel</Button>,
        <Button key="save" variant="primary" loading={save.isPending} onClick={() => form.submit()}>Update</Button>,
      ]
    : [
        tabIndex > 0 ? <Button key="back" variant="default" onClick={() => setTab(TAB_KEYS[tabIndex - 1])}>Back</Button> : null,
        <Button key="cancel" variant="default" onClick={onClose}>Cancel</Button>,
        isLastTab
          ? <Button key="create" variant="primary" loading={save.isPending} onClick={() => form.submit()}>Create</Button>
          : <Button key="next" variant="primary" onClick={goNext}>Next</Button>,
      ];

  // Seed the edit form via initialValues (record is available synchronously at open time) so the Form
  // mounts already-populated — no setFieldsValue racing the Radix-modal remount, no stale leak.
  const initialValues = record
    ? {
        ...record,
        territoryId: record.territoryId ? String(record.territoryId) : undefined,
        assignedSalesPersonId: record.assignedSalesPersonId ? String(record.assignedSalesPersonId) : undefined,
        creditLimit: Number(record.creditLimit ?? 0),
        creditPeriodDays: Number(record.creditPeriodDays ?? 0),
        discountType: record.discountType ?? 'CUSTOM',
        generalDiscountPct: Number(record.generalDiscountPct ?? 0),
        latitude: Number(record.latitude ?? 0),
        longitude: Number(record.longitude ?? 0),
        agreementDate: record.agreementDate ? dayjs(record.agreementDate) : undefined,
        agreementExpiry: record.agreementExpiry ? dayjs(record.agreementExpiry) : undefined,
      }
    : undefined;

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isEdit ? `Edit retailer · ${record?.code}` : 'New retailer'}
      width={1040}
      footer={footer}
    >
      <Form
        key={isEdit ? `edit-${record?.id}` : 'create'}
        form={form}
        initialValues={initialValues}
        layout="vertical"
        onFinish={(v) => {
          if (!isEdit) { save.mutate(toPayload(v)); return; }
          const patch = changedFields(toPayload(initialValues ?? {}), toPayload(v), Object.keys(v));
          if (!Object.keys(patch).length) { message.info('Nothing has changed.'); return; }
          save.mutate(patch);
        }}
        onFinishFailed={({ errorFields }) => {
          const first = errorFields?.[0]?.name?.[0] as string | undefined;
          if (first) setTab(fieldTab(first));
          message.warning('Please complete the highlighted required fields.');
        }}
      >
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: 'basics',
              label: 'Basics',
              children: (
                <Row gutter={16}>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item name="code" label="Code" extra={isEdit ? undefined : 'Leave blank to auto-assign'}>
                      <Input placeholder={isEdit ? undefined : 'Auto-assigned'} disabled={isEdit} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="companyName" label="Company Name" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      name="contactPerson"
                      label="Owner / Contact Name"
                      tooltip="The person's name — used across the app (orders, returns, activity) to identify this retailer. Don't put the phone number here."
                      rules={[{ required: true }]}
                    >
                      <Input placeholder="e.g. Ramesh Kumar" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="mobile" label="Mobile (WhatsApp)" rules={mobileRules()}
                      tooltip="The single number for this retailer — used for WhatsApp OTP login and all WhatsApp contact.">
                      <PhoneInput placeholder="Phone number" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="email" label="Email" rules={emailRules}>
                      <Input type="email" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="gstNumber" label="GST Number" rules={gstinRules} normalize={upperCase}>
                      <Input placeholder="27ABCDE1234F1Z5" maxLength={15} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="panNumber" label="PAN" rules={panRules} normalize={upperCase}>
                      <Input placeholder="ABCDE1234F" maxLength={10} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="agriLicenseNo" label="Agri License No." normalize={upperCase}>
                      <Input placeholder="Fertiliser / seed / pesticide licence" maxLength={40} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    {/* ACTIVE is offered ONLY to a KYC verifier. For everyone else it isn't a choice
                        in a dropdown any more — they collect the documents and hit "Submit for KYC"
                        on the retailer page, and an Admin activates by verifying. The server enforces
                        this on the PATCH regardless of what the form renders. */}
                    {/* On an EDIT the status is not this form's to change: PATCH /retailers/:id refuses it for everyone
                        (it is server-owned). Activation is a KYC decision; suspension is the retailer list's row action.
                        So an edit shows it read-only and says where it is changed, instead of letting a save fail. */}
                    <Form.Item name="status" label="Status" initialValue={isEdit ? undefined : 'KYC_PENDING'}
                      extra={isEdit
                        ? 'Changed by KYC verification, or from the retailer list’s row menu (Activate / Deactivate).'
                        : (canVerifyKyc ? undefined : 'Activation happens through KYC verification.')}>
                      <Select disabled={isEdit} options={STATUS_OPTIONS.filter((o) => canVerifyKyc || o.value !== 'ACTIVE' || record?.status === 'ACTIVE')} />
                    </Form.Item>
                  </Col>
                </Row>
              ),
            },
            {
              key: 'location',
              label: 'Location & Territory',
              children: (
                <Row gutter={16}>
                  {/* Map fills coordinates + auto-fills address. Mounted only when this
                      tab is active so Leaflet sizes correctly (the fields below stay
                      rendered for validation). */}
                  <Col span={24}>
                    {tab === 'location' && (
                      <LocationPicker
                        fields={{ lat: 'latitude', lng: 'longitude', address: 'addressLine1', city: 'city', pincode: 'pincode', state: 'stateCode' }}
                        required
                      />
                    )}
                  </Col>
                  <Col span={24}>
                    <Form.Item name="addressLine1" label="Address line 1" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="city" label="City" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="stateCode" label="State" rules={[{ required: true }]}>
                      <Select showSearch optionFilterProp="label" options={STATE_OPTIONS} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="pincode" label="Pincode" rules={pincodeRules()}>
                      <Input maxLength={6} />
                    </Form.Item>
                  </Col>

                  {/* Coordinates come from the map picker above — hidden required fields. */}
                  <Form.Item name="latitude" hidden rules={[{ required: true, message: 'Pick a location on the map' }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="longitude" hidden rules={[{ required: true, message: 'Pick a location on the map' }]}>
                    <Input />
                  </Form.Item>

                  <Col xs={24} md={12}>
                    <Form.Item name="territoryId" label="Territory" rules={[{ required: true }]}
                      extra="Districts & beats only.">
                      <Select
                        showSearch
                        optionFilterProp="label"
                        options={territoryOptions}
                        onChange={() => form.setFieldValue('assignedSalesPersonId', undefined)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="assignedSalesPersonId" label="Assigned Sales Person"
                      extra={territoryId
                        ? (scopedSps ? 'Only Sales Persons who work the selected territory.' : (!spLoading ? 'No Sales Person works this territory yet — showing all.' : undefined))
                        : 'Pick a territory to filter Sales Persons.'}>
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        options={spOptions}
                        notFoundContent={territoryId && spLoading ? 'Loading…' : undefined}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item name="creditLimit" label="Credit Limit (₹)" initialValue={0}>
                      <InputNumber style={{ width: '100%' }} min={0} step={1000} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item name="creditPeriodDays" label="Credit Period (days)" initialValue={0}>
                      <InputNumber style={{ width: '100%' }} min={0} step={1} />
                    </Form.Item>
                  </Col>

                  <Col span={24}><Divider style={{ margin: '4px 0 8px' }} orientation="left">Discount policy</Divider></Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="discountType" label="Discount mode" initialValue="CUSTOM"
                      extra={discountType === 'GENERAL'
                        ? 'Fixed % applied + locked on every order.'
                        : 'Discount set per product while placing the order.'}>
                      <Select options={[
                        { value: 'CUSTOM', label: 'Customize (per order)' },
                        { value: 'GENERAL', label: 'General (fixed %)' },
                      ]} />
                    </Form.Item>
                  </Col>
                  {discountType === 'GENERAL' && (
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item name="generalDiscountPct" label="Fixed discount %" initialValue={0}
                        rules={[{ required: true, message: 'Enter the fixed discount %' }]}>
                        <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} addonAfter="%" />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              ),
            },
            {
              key: 'documents',
              label: 'Documents & Agreement',
              children: (
                <Row gutter={16}>
                  <Col span={24}><Divider style={{ margin: '4px 0 8px' }} orientation="left">Documents</Divider></Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="photoUrl" label="Shop photo">
                      <FileUploadField category="retailer-photo" buttonLabel="Upload shop photo" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="gstCertUrl" label="GST certificate"
                      extra="PDF or photo — auto-shown to auditors on the retailer 360 page.">
                      <FileUploadField category="attachment" buttonLabel="Upload GST cert" preview={false} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="panCardUrl" label="PAN card">
                      <FileUploadField category="attachment" buttonLabel="Upload PAN" preview={false} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    {/* The licence DOCUMENT — "Agri License No." on the Basics tab is only the number.
                        For a fertiliser / seed / pesticide dealer this is the statutory paper, so KYC
                        verification requires the scan. */}
                    <Form.Item name="agriLicenseUrl" label="Agri licence document"
                      extra="Fertiliser / seed / pesticide licence — required for KYC.">
                      <FileUploadField category="attachment" buttonLabel="Upload licence" preview={false} />
                    </Form.Item>
                  </Col>

                  <Col span={24}><Divider style={{ margin: '4px 0 8px' }} orientation="left">Dealer agreement</Divider></Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="agreementUrl" label="Agreement copy" extra="Signed dealer agreement (PDF/photo).">
                      <FileUploadField category="attachment" buttonLabel="Upload agreement" preview={false} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="agreementDate" label="Agreement date">
                      <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="agreementExpiry" label="Valid till (expiry)"
                      extra="Row turns red as expiry nears. Renew later from the retailer page.">
                      <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                    </Form.Item>
                  </Col>
                </Row>
              ),
            },
          ]}
        />
      </Form>
    </Modal>
  );
}
